import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface PayrollTrendMonth {
  period: string
  periodLabel: string
  headcount: number
  totalCprov: number
  totalCrel: number
  totalSalary: number
  totalBenefits: number
  totalTaxes: number
  totalProvisions: number
  avgCostPerEmployee: number
}

interface PayrollTrendChartProps {
  data: PayrollTrendMonth[] | undefined
  isLoading: boolean
}

const formatCurrency = (value: number) => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}k`
  return `R$ ${value.toFixed(0)}`
}

export function PayrollTrendChart({ data, isLoading }: PayrollTrendChartProps) {
  if (isLoading) {
    return <ChartSkeleton />
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
        <div className="mb-6">
          <h3 className="text-white font-display text-xl font-semibold mb-1">Tendência de Folha</h3>
          <p className="text-brand-gray font-body text-sm">Custo provisionado vs realizado nos últimos 12 meses</p>
        </div>
        <div className="flex items-center justify-center h-[300px]">
          <p className="text-brand-gray font-body text-sm">Nenhum dado disponível</p>
        </div>
      </div>
    )
  }

  // Data comes DESC from backend — reverse for chronological display
  const chartData = [...data].reverse()

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold mb-1">Tendência de Folha</h3>
        <p className="text-brand-gray font-body text-sm">Custo provisionado vs realizado nos últimos 12 meses</p>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2D34" />
          <XAxis
            dataKey="periodLabel"
            stroke="#838998"
            tick={{ fontSize: 11, fill: '#838998' }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#838998"
            tick={{ fontSize: 11, fill: '#838998' }}
            tickFormatter={formatCurrency}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div
                    style={{
                      backgroundColor: '#24262B',
                      border: '1px solid #2A2D34',
                      borderRadius: '12px',
                      padding: '10px',
                      color: '#ffffff',
                    }}
                  >
                    <p style={{ marginBottom: '5px', fontWeight: 600 }}>{label}</p>
                    {payload.map((entry: any, idx: number) => (
                      <p key={idx} style={{ color: entry.color, marginBottom: '3px' }}>
                        {entry.name}: {formatCurrency(entry.value)}
                      </p>
                    ))}
                  </div>
                )
              }
              return null
            }}
          />
          <Legend wrapperStyle={{ fontSize: '13px', color: '#ffffff' }} />
          <Line
            type="monotone"
            dataKey="totalCprov"
            stroke="#D2FA52"
            strokeWidth={2}
            dot={{ fill: '#D2FA52', r: 3 }}
            name="Provisionado (cprov)"
          />
          <Line
            type="monotone"
            dataKey="totalCrel"
            stroke="#EF4444"
            strokeWidth={2}
            dot={{ fill: '#EF4444', r: 3 }}
            name="Realizado (crel)"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function ChartSkeleton() {
  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 animate-pulse shadow-monumenta">
      <div className="mb-6">
        <div className="h-6 w-40 bg-brand-dark-lighter rounded mb-2" />
        <div className="h-4 w-72 bg-brand-dark-lighter rounded" />
      </div>
      <div className="h-[300px] bg-brand-dark-lighter rounded" />
    </div>
  )
}
